import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ContentStorageService } from '@ptl/global-components';
import { Observable } from 'rxjs';
import { ReducerUtilityService } from '../../../shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../configs/interfaces/plansStateStructure.interface';
import { PRODUCT_GROUPS } from '../enums/product-groups.enum';
import { PlanDetailsDocumentsService } from './plan-details-documents.service';

/****
  see plan-docs-by-county.component.ts 
****/
const PRODUCT_HEADING_DESC = 'product-heading-desc';
const PLAN_DOCS_INPUT_KEYS = ['service-path', 'doc-root', 'plan-tags', PRODUCT_HEADING_DESC, 'plan-heading-desc', 'tag-path-querys', 'static-links'];
const PLAN_DOCS_TAG_NAME = 'sxe-plan-docs-by-county';
const NO_DOCUMENTS_FOUND_TEMPLATE = '<p class="no-documents-msg">The documentation requested was not reachable. Please try again later.</p>';
const PLAN_DETAILS_STORAGE_KEY = 'planDetailsDocumentsContent';

@Component({
    selector: 'app-plan-details-documents',
    templateUrl: './plan-details-documents.component.html',
    styleUrls: ['./plan-details-documents.component.scss'],
    standalone: false
})
export class PlanDetailsDocumentsComponent implements OnInit {

  @Input() tabChange: Observable<void>;

  currentYear: string;
  fullPageHtml: string = '';
  planDocsTagHtml: SafeHtml = null;
  planName: string = null;
  overrideBasePlanMap: any = {};
  medigapPlanName: string = 'Medigap Blue';

  constructor(
    private planDocsService: PlanDetailsDocumentsService,
    private reducerUtilityService: ReducerUtilityService,
    private contentStorageService: ContentStorageService,
    private sanitizer: DomSanitizer
  ) { }

  async ngOnInit() {
    this.contentStorageService.contentChanged$.subscribe(response => {
      const config = response?.get(PLAN_DETAILS_STORAGE_KEY);
      if (config) {
        this.planDocsService.initConfig(config);

        let contentOverrideBasePlanMap = config.get('overrideBasePlanMap');
        let contentMedigapPath = config.get('medigapPlanName');
        this.overrideBasePlanMap = contentOverrideBasePlanMap ? JSON.parse(contentOverrideBasePlanMap) : this.overrideBasePlanMap;
        this.medigapPlanName = contentMedigapPath ? contentMedigapPath : this.medigapPlanName;

        this.loadDocuments();
      }
    });

    this.tabChange.subscribe(() => {
      this.rebuildHtml();
    });
  }

  loadDocuments() {
    const plansState: PlansStateStructure = this.reducerUtilityService.getState('Plans');
    this.planName = plansState?.medicareAdvantage_or_PDP_planForDRX?.productName;
    this.currentYear = plansState?.medicareAdvantage_or_PDP_planForDRX?.year;
    
    if (plansState.activeProductGroup === PRODUCT_GROUPS.MEDIGAP) {
      this.planName = this.medigapPlanName;
    }

    this.planDocsService.getDocumentPageFromPlanName(this.planName).then((content) => {
      this.fullPageHtml = content;
      let finalHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(this.parseSxeTagFromHtml(content));
      this.planDocsTagHtml = finalHtml ? finalHtml : NO_DOCUMENTS_FOUND_TEMPLATE;
    })
    .catch((e) => {
      this.planDocsTagHtml = NO_DOCUMENTS_FOUND_TEMPLATE;
    });
  }

  parseSxeTagFromHtml(fullHtml: string): string {
    let domParser = new DOMParser();
    let doc = domParser.parseFromString(fullHtml, 'text/html');
    let planDocTags = doc?.getElementsByTagName(PLAN_DOCS_TAG_NAME);

    // Filter through all found tags for one that matches our current plan year
    if (planDocTags?.length) {
      for (let i = 0; i < planDocTags.length; i++) {
        let attrYear = planDocTags[i].getAttribute('year');
        let attrHeading = planDocTags[i].getAttribute(PRODUCT_HEADING_DESC);

        // If we have a year attribute set, only compare against that
        // If no year is set, then compare the heading text for year value
        if (attrYear == this.currentYear || (!attrYear && attrHeading && attrHeading.indexOf(this.currentYear) !== -1)) {
          return this.getCleanElementFromSource(planDocTags[i]).outerHTML;
        }
      }
    }

    return NO_DOCUMENTS_FOUND_TEMPLATE;
  }

  // rebuild plan docs tag from scratch using only the neccessary source attributes, for security purposes
  getCleanElementFromSource(source: Element): HTMLElement {
    let finalTag = document.createElement(PLAN_DOCS_TAG_NAME);

    for (let index in PLAN_DOCS_INPUT_KEYS) {
      let attrValue = source.getAttribute(PLAN_DOCS_INPUT_KEYS[index]);
      finalTag.setAttribute(PLAN_DOCS_INPUT_KEYS[index], attrValue);
    }

    // Also add the plan name as an attribute so we only show specific plan docs for that plan
    // Check the content storage map if we have an override value for this plan's "base" plan
    let basePlanName = this.overrideBasePlanMap[this.planName?.toLowerCase()];
    finalTag.setAttribute('plan-names', JSON.stringify([
      basePlanName ? basePlanName : this.planDocsService.getBasePlanName(this.planName),
      this.planName
    ]));

    return finalTag;
  }

  // Due to an odd bug that occurs when switching between tabs, we have to re-trust the html
  // After being hidden, the html that was previously marked "safe" is no longer seen that way, despite the value being the exact same SafeHtml object
  // This causes unsafe tags, specfically our sxe-plan-docs tag to be stripped out and so nothing is rendered on the second visit
  rebuildHtml() {
    this.planDocsTagHtml = this.sanitizer.bypassSecurityTrustHtml(this.parseSxeTagFromHtml(this.fullPageHtml));
  }
}
